import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export type KioskCategoryPagePreviewProduct = {
  id: string;
  _id: string;
  shop_id: string;
  name: string;
  description?: string;
  price: number;
  final_price: number;
  vat: string;
  additions_group: string;
  categories: readonly unknown[];
  additions: { products: readonly unknown[] };
  images?: {
    cover?: { url: string; width?: number; height?: number };
    thumbnail?: { url: string; width?: number; height?: number };
  };
  available?: boolean;
  quantity?: number;
  type?: string;
  allergens?: readonly unknown[];
};

export type KioskCategoryPagePreviewCategory = {
  id: string;
  name: string;
  icon?: string;
  level?: number;
  hasDescendantWithVisibleProducts?: boolean;
  productsInformation: readonly KioskCategoryPagePreviewProduct[];
};

/**
 * Serializable preview content for `KioskCategoryPageComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type KioskCategoryPageScenarioContent = {
  categoryId: string;
  categoryWithProducts: KioskCategoryPagePreviewCategory | null;
  subCategoriesWithProducts: readonly KioskCategoryPagePreviewCategory[];
};
export const KIOSKCATEGORYPAGE_LEGACY_FIELD_ORDER = [
  {
    type: 'heading',
    value: 'pages.blocks.kiosk-category-page.fieldset.product-card.label',
    level: 2,
  },
  'text_color',
  'card_color',
  'price_color',
  'secondary_color',
  {
    type: 'heading',
    value: 'pages.blocks.kiosk-category-page.fieldset.subcategory-tabs.label',
    level: 2,
  },
  'categories_background_color',
  'categories_active_color',
  'categories_inactive_color',
  {
    type: 'heading',
    value: 'pages.blocks.kiosk-category-page.fieldset.add-button.label',
    level: 2,
  },
  'button_background_color',
  'button_text_color',
  {
    type: 'heading',
    value: 'pages.blocks.kiosk-category-page.fieldset.unavailable-button.label',
    level: 2,
  },
  'unavailable_button_background',
  'unavailable_button_text',
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskCategoryPageDescriptor: _generatedKioskCategoryPageDescriptor,
  kioskCategoryPageBlocDefinition,
  kioskCategoryPageDashboardLayout,
  kioskCategoryPageConfig,
  kioskCategoryPageResolveConfig,
  kioskCategoryPageProperties,
  kioskCategoryPageDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: 'Kiosk - Category page',
    internal_name: 'Kiosk - Category page',
    url: 'category/:categoryId',
    defaultParent: [/^Kiosk - Shop page$/i],
    componentType: 'KioskCategoryPageComponent',
    category: 'page',
    description: 'Kiosk - Category page.',
    keywords: [],
    isRootBlock: true,
    sharedFields: {},
  },
  () => ({
    text_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#000' },
      options: {
        label: 'pages.blocks.kiosk-category-page.text-color.label',
      },
    }),
    card_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-100' },
      options: {
        label: 'pages.blocks.kiosk-category-page.card-color.label',
      },
    }),
    price_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-900' },
      options: {
        label: 'pages.blocks.kiosk-category-page.price-color.label',
      },
    }),
    secondary_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-200' },
      options: {
        label: 'pages.blocks.kiosk-category-page.secondary-color.label',
        help: 'pages.blocks.kiosk-category-page.secondary-color.help',
      },
    }),
    button_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.kiosk-category-page.button-background-color.label',
      },
    }),
    button_text_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-50' },
      options: {
        label: 'pages.blocks.kiosk-category-page.button-text-color.label',
      },
    }),
    unavailable_button_background: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-200' },
      options: {
        label: 'pages.blocks.kiosk-category-page.unavailable-button-background.label',
      },
    }),
    unavailable_button_text: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.kiosk-category-page.unavailable-button-text.label',
      },
    }),
    categories_background_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#FFFFFF' },
      options: {
        label: 'pages.blocks.kiosk-category-page.categories-background-color.label',
      },
    }),
    categories_active_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'primary-600' },
      options: {
        label: 'pages.blocks.kiosk-category-page.categories-active-color.label',
        help: 'pages.blocks.kiosk-category-page.categories-active-color.help',
      },
    }),
    categories_inactive_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.kiosk-category-page.categories-inactive-color.label',
        help: 'pages.blocks.kiosk-category-page.categories-inactive-color.help',
      },
    }),
  }),
  () => ({
    tabs: [
      {
        label: 'pages.blocks.kiosk-category-page.tab.content.label',
        fieldsets: [
          {
            legend: 'pages.blocks.kiosk-category-page.fieldset.product-card.label',
            fields: ['text_color', 'card_color', 'price_color', 'secondary_color'],
          },
          {
            legend: 'pages.blocks.kiosk-category-page.fieldset.subcategory-tabs.label',
            fields: [
              'categories_background_color',
              'categories_active_color',
              'categories_inactive_color',
            ],
          },
          {
            legend: 'pages.blocks.kiosk-category-page.fieldset.add-button.label',
            fields: ['button_background_color', 'button_text_color'],
          },
          {
            legend: 'pages.blocks.kiosk-category-page.fieldset.unavailable-button.label',
            fields: ['unavailable_button_background', 'unavailable_button_text'],
          },
        ],
      },
    ],
  })
);

export function kioskCategoryPageDescriptor(): IBlockDescriptor;
export function kioskCategoryPageDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskCategoryPageDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskCategoryPageDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskCategoryPageDescriptor,
    kioskCategoryPageProperties,
    fieldOrder,
  );
}

export {
  kioskCategoryPageBlocDefinition,
  kioskCategoryPageDashboardLayout,
  kioskCategoryPageProperties,
  kioskCategoryPageConfig,
  kioskCategoryPageResolveConfig,
  kioskCategoryPageDefinitionSetupScenarios,
};

export type KioskCategoryPageConfig = typeof kioskCategoryPageConfig;
